import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import DaringFuturesChallenger from './DaringFuturesChallenger';
import DiagnosticSelector from './pages/DiagnosticSelector';
import TruthTellersRequest from './pages/TruthTellersRequest';
import DiagnosticContainer from './components/Diagnostic/DiagnosticContainer';
// DiagnosticDynamic retired - archived to _archive folder (replaced by AdminDynamicResults)
import LeadershipBurdenDiagnostic from './pages/LeadershipBurdenDiagnostic';
import PsychologicalFlexibilityDiagnostic from './pages/PsychologicalFlexibilityDiagnostic';
import SystemBurdenDiagnostic from './pages/diagnostics/SystemBurdenDiagnostic';
import SystemBurdenResults from './pages/diagnostics/SystemBurdenResults';
import LeadershipBurdenDiagnosticV7 from './pages/diagnostics/LeadershipBurdenDiagnostic';
import LeadershipBurdenResults from './pages/diagnostics/LeadershipBurdenResults';
import PsychologicalFlexibilityDiagnosticV7 from './pages/diagnostics/PsychologicalFlexibilityDiagnostic';
import PsychologicalFlexibilityResults from './pages/diagnostics/PsychologicalFlexibilityResults';
import SystemBurdenResultsTest from './pages/SystemBurdenResultsTest';
import LeadershipBurdenResultsTest from './pages/LeadershipBurdenResultsTest';
import HubSpotTest from './components/HubSpotTest';
import DatabaseTest from './pages/DatabaseTest';
import { pageview } from './utils/analytics';
import { loadDiagnosticSettings } from './utils/algorithms';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminReports from './pages/AdminReports';
import AdminDatabaseManagement from './pages/AdminDatabaseManagement';
import AdminSettings from './pages/AdminSettings';
import AdminDynamicResults from './pages/AdminDynamicResults';

function AnalyticsWrapper({ children }) {
  const location = useLocation();

  useEffect(() => {
    const pageTitles = {
      '/': 'Home - Daring Futures',
      '/request-diagnostic': 'Request Diagnostic',
      '/request-truth': 'Truth Tellers - Request Diagnostic',
      '/diagnostic': 'Hidden Work Diagnostic',
      '/leadership-burden': 'Leadership Burden Diagnostic',
      '/psychological-flexibility': 'Psychological Flexibility Diagnostic',
      '/diagnostics/system-burden/results': 'System Burden Results',
      '/admin/dashboard': 'Admin Dashboard',
      '/admin/reports': 'Admin Reports',
      '/admin/dynamic-results': 'Admin Dynamic Results',
      '/admin/database': 'Admin Database',
      '/admin/settings': 'Admin Settings',
      '/hubspot-test': 'HubSpot Test Harness',
      '/database-test': 'Database Test Harness'
    };

    const title = pageTitles[location.pathname] || 'Daring Futures';
    pageview(location.pathname, title);
  }, [location]);

  return children;
}

function App() {
  // Load diagnostic settings from database on app initialization (v5.0.0+)
  useEffect(() => {
    loadDiagnosticSettings().catch(error => {
      console.error('Failed to initialize diagnostic settings:', error);
    });
  }, []);

  return (
    <Router>
      <AuthProvider>
        <AnalyticsWrapper>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<DaringFuturesChallenger />} />
            <Route path="/request-diagnostic" element={<DiagnosticSelector />} />
            <Route path="/request-truth" element={<TruthTellersRequest />} />
            <Route path="/diagnostic" element={<DiagnosticContainer />} />
            <Route path="/leadership-burden" element={<LeadershipBurdenDiagnostic />} />
            <Route path="/psychological-flexibility" element={<PsychologicalFlexibilityDiagnostic />} />

            {/* v7.0.0 Diagnostic Routes */}
            <Route path="/diagnostics/system-burden/:code" element={<SystemBurdenDiagnostic />} />
            <Route path="/diagnostics/system-burden/results/:code" element={<SystemBurdenResults />} />
            <Route path="/diagnostics/leadership-burden/:code" element={<LeadershipBurdenDiagnosticV7 />} />
            <Route path="/diagnostics/leadership-burden/results/:code" element={<LeadershipBurdenResults />} />
            <Route path="/diagnostics/psychological-flexibility/:code" element={<PsychologicalFlexibilityDiagnosticV7 />} />
            <Route path="/diagnostics/psychological-flexibility/results/:code" element={<PsychologicalFlexibilityResults />} />

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/reports"
              element={
                <ProtectedRoute>
                  <AdminReports />
                </ProtectedRoute>
              }
            />
            {/* Dynamic Diagnostic route retired - replaced by /admin/dynamic-results */}
            <Route
              path="/admin/database"
              element={
                <ProtectedRoute requiresFullAdmin={true}>
                  <AdminDatabaseManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute requiresFullAdmin={true}>
                  <AdminSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/dynamic-results"
              element={
                <ProtectedRoute>
                  <AdminDynamicResults />
                </ProtectedRoute>
              }
            />

            {/* Test harness for HubSpot API development */}
            <Route path="/hubspot-test" element={<HubSpotTest />} />

            {/* Test harness for D1 database connection */}
            <Route path="/database-test" element={<DatabaseTest />} />

            {/* Test harness for v7.0.0 System Burden Results */}
            <Route path="/test-system-burden-results" element={<SystemBurdenResultsTest />} />

            {/* Test harness for v7.0.0 Leadership Burden Results */}
            <Route path="/test-leadership-burden-results" element={<LeadershipBurdenResultsTest />} />
          </Routes>
        </AnalyticsWrapper>
      </AuthProvider>
    </Router>
  );
}

export default App;